export const NAV_LINKS = [
  { label: "Services", href: "services" },
  { label: "Process", href: "process" },
  { label: "Work", href: "work" },
  { label: "About", href: "about" },
];

export const CTA_OPTIONS = [
  {
    id: "website",
    icon: "language",
    label: "My Website",
    sublabel: "Build or improve a site",
    serviceKey: "audit",
    headline: "AI built your site. But does it work for real users?",
    insight:
      "Most small business websites lose customers silently , through confusing navigation, unclear value props, and layouts that feel off. AI can generate pages; it cannot study your specific visitors.",
    stats: [
      { num: "70%", desc: "of online businesses fail due to poor usability" },
      { num: "88%", desc: "of users never return after a bad experience" },
      { num: "94%", desc: "of first impressions are design-related" },
      { num: "$1→$10", desc: "return for every dollar invested in UX" },
    ],
  },
  {
    id: "product",
    icon: "devices",
    label: "My Product",
    sublabel: "SaaS, app or digital tool",
    serviceKey: "workflow",
    headline: "Your product is live , but users are quietly dropping off.",
    insight:
      "MVP-stage products often ship technically sound but experientially broken. We fix the friction points before they become churn , using real user evidence, not guesswork.",
    stats: [
      { num: "228%", desc: "higher ROI with Human-Centered Design" },
      { num: "1 in 3", desc: "users leave after one confusing interaction" },
      { num: "68%", desc: "of SaaS churn tied to poor onboarding" },
      { num: "6×", desc: "costlier to win a new customer than keep one" },
    ],
  },
  {
    id: "startup",
    icon: "rocket_launch",
    label: "My Startup",
    sublabel: "Pre-MVP or early stage",
    serviceKey: "kickstart",
    headline: "Build the right thing before it's too expensive to change.",
    insight:
      "Most startups fail not because they built badly , but because they built the wrong thing. We validate your assumptions with real users before your dev team writes a single line.",
    stats: [
      { num: "42%", desc: "of startups fail , nobody wanted what they built" },
      { num: "5 users", desc: "reveal ~85% of critical usability problems" },
      { num: "100×", desc: "cheaper to fix a UX issue before launch" },
      { num: "2–3 wks", desc: "from rough idea to tested, validated flows" },
    ],
  },
  {
    id: "career",
    icon: "school",
    label: "UX Career Journey",
    sublabel: "Break into UX design",
    serviceKey: "support",
    headline: "Great UX is a practice, not a one-time project.",
    insight:
      "Small consistent improvements compound into major results. We become your embedded UX team , weekly improvements, research, and dev fixes , without adding headcount.",
    stats: [
      {
        num: "$400/mo",
        desc: "starting , less than one day of agency billing",
      },
      { num: "81%", desc: "of consumers pay more for a better experience" },
      {
        num: "UX+Dev",
        desc: "combined , vanishingly rare at this price point",
      },
      {
        num: "Weekly",
        desc: "improvements, requests and dev fixes every month",
      },
    ],
  },
];

export const SERVICES = [
  {
    id: "audit",
    number: "01",
    name: "UX Heuristic Audit",
    tagline: "Your fastest path to knowing what's broken.",
    price: "$300 – $800",
    time: "1–3 days",
    icon: "search_insights",
    description:
      "A rigorous review of your site or product against proven UX heuristics. We identify what's costing you users, clarity, and conversions , and hand you a prioritised fix list you can act on immediately.",
    deliverables: [
      "10–15 page audit report",
      "Heuristics analysis against Nielsen's 10 principles",
      "Usability issues mapped by severity",
      "Quick-wins list you can act on immediately",
      "Prioritised action plan with rationale",
    ],
    cta: "Get an Audit",
  },
  {
    id: "workflow",
    number: "02",
    name: "Workflow Improvement",
    tagline: "For products that work , but feel clunky.",
    price: "$800 – $2,500",
    time: "1–2 weeks",
    icon: "account_tree",
    description:
      "End-to-end flow mapping, pain point analysis, and redesigned user journeys. We fix the experience without blowing up what already works , and without a month-long engagement.",
    deliverables: [
      "End-to-end flow map of current state",
      "Pain point analysis with user impact scores",
      "Revised, streamlined workflow design",
      "Component-level redesigns where needed",
      "Optional dev implementation of quick fixes",
    ],
    cta: "Fix My Workflow",
  },
  {
    id: "kickstart",
    number: "03",
    name: "Startup UX Kickstart",
    tagline: "Validate before you build.",
    price: "$1,200 – $3,500",
    time: "2–3 weeks",
    icon: "rocket_launch",
    description:
      "For pre-product founders who need direction. We take your idea from concept to validated, testable flows , so your dev team builds the right thing from day one.",
    deliverables: [
      "User research & persona synthesis",
      "Lightweight guided user interviews",
      "Concept shaping & information architecture",
      "Core screen designs",
      "Usability validation with 3–5 real users",
    ],
    cta: "Kickstart My UX",
  },
  {
    id: "research",
    number: "04",
    name: "User Research & Testing",
    tagline: "Decisions backed by real user evidence.",
    price: "$500 – $2,000",
    time: "1–2 weeks",
    icon: "person_search",
    description:
      "Small teams rarely do research , we make it easy. A lean, focused study that answers your biggest user questions with evidence you can actually act on.",
    deliverables: [
      "Custom research plan",
      "Lightweight user interviews (remote or in-person)",
      "3–5 usability test sessions",
      "Synthesis of key insights",
      "Actionable recommendations report",
    ],
    cta: "Start Research",
  },
  {
    id: "implementation",
    number: "05",
    name: "UX + Dev Implementation",
    tagline: "We don't just design it , we build it.",
    price: "$500 – $8,000",
    time: "1–4 weeks",
    icon: "code",
    description:
      "Add-on to any audit or workflow package. Our developer turns your UX improvements into shipped code , fixing top issues, updating UI components, and integrating redesigned flows.",
    deliverables: [
      "Fix top-priority UX issues in code",
      "UI component updates",
      "Redesigned flow implementation",
      "Performance improvements",
      "Basic feature integrations",
    ],
    cta: "Add Dev Work",
  },
  {
    id: "support",
    number: "06",
    name: "Monthly UX Support",
    tagline: "Your embedded UX team. No headcount required.",
    price: "$400 – $1,500 /mo",
    time: "Ongoing",
    icon: "support_agent",
    description:
      "Stable, recurring UX partnership for teams who want continuous improvement without hiring. Small but compounding wins every single month.",
    deliverables: [
      "Weekly small UX improvements",
      "1–2 design requests per month",
      "1 mini research or usability session",
      "Developer fixes included",
      "Priority turnaround & async support",
    ],
    cta: "Get Monthly Support",
  },
];

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Discovery Call",
    duration: "15 min · Free",
    desc: "We understand your situation, goals, and constraints. No jargon, no pitch , just a clear conversation about whether we're the right fit.",
  },
  {
    number: "02",
    title: "Scoped Proposal",
    duration: "24 hrs",
    desc: "You receive a fixed-price proposal with clear deliverables, timeline, and what success looks like. No surprises, no hidden costs.",
  },
  {
    number: "03",
    title: "Research & Analysis",
    duration: "Days 1–5",
    desc: "We study your users, your product, and your competitive context. Every recommendation is grounded in evidence, not opinion.",
  },
  {
    number: "04",
    title: "Design & Iteration",
    duration: "Days 5–14",
    desc: "We design, share, refine. You see progress in real time and give feedback at every stage , no black-box delivery.",
  },
  {
    number: "05",
    title: "Handoff or Build",
    duration: "Final days",
    desc: "Detailed specs, annotated designs, and an optional dev build so your team knows exactly what to implement , or we implement it ourselves.",
  },
];

export const CASE_STUDIES = [
  {
    id: "saas-onboarding",
    tag: "SaaS Product",
    title: "Cutting onboarding drop-off by 61% for a B2B analytics tool",
    service: "Workflow Improvement",
    duration: "2 weeks",
    result: "61% reduction in trial-to-paid drop-off",
    description:
      "A B2B analytics startup was seeing 70% of trial users abandon during onboarding. We mapped the full flow, identified 4 critical friction points, and redesigned the first-run experience around real user mental models.",
    metrics: [
      { label: "Drop-off reduced", value: "61%" },
      { label: "Activation rate", value: "+44%" },
      { label: "Timeline", value: "2 wks" },
    ],
  },
  {
    id: "clinic-website",
    tag: "Service Business",
    title: "A physiotherapy clinic doubled their online bookings in 3 weeks",
    service: "UX Audit + Implementation",
    duration: "3 weeks",
    result: "2× online booking conversion rate",
    description:
      "Their AI-generated website looked clean but buried the booking CTA and confused new visitors with too many competing messages. A heuristic audit plus targeted dev implementation fixed it fast.",
    metrics: [
      { label: "Booking uplift", value: "2×" },
      { label: "Bounce rate", value: "−38%" },
      { label: "Budget", value: "$1,400" },
    ],
  },
  {
    id: "startup-mvp",
    tag: "Early Startup",
    title:
      "Validating a fintech MVP with 5 users before writing a single line of code",
    service: "Startup UX Kickstart",
    duration: "2.5 weeks",
    result: "Core assumptions invalidated → pivot saved ~$40k in dev",
    description:
      "A fintech founder had a clear vision and was ready to build. We ran 5 user sessions first. Three core assumptions were wrong. The pivot shaped a product users actually wanted , and saved months of rework.",
    metrics: [
      { label: "Dev cost saved", value: "$40k+" },
      { label: "User sessions", value: "5" },
      { label: "Timeline", value: "2.5 wks" },
    ],
  },
];

export const TEAM = [
  {
    name: "Lead UX Designer",
    role: "Strategy & Research",
    bio: "8 years designing for startups and scale-ups. Believes research is the best investment any small team can make , and that great UX should not be reserved for funded companies.",
    initial: "U",
  },
  {
    name: "UX Engineer",
    role: "Design & Development",
    bio: "Rare hybrid who designs and builds. Closes the gap between a polished spec and a shipped feature , so clients get implementation, not just recommendations.",
    initial: "X",
  },
];

export const FAQS = [
  {
    q: "Do I need a big budget to work with you?",
    a: "No. Our entry service starts at $300. We deliberately built productised packages so small businesses can access real UX expertise without agency-level costs.",
  },
  {
    q: "Can't AI tools just handle UX now?",
    a: "AI can generate layouts and write copy , but it cannot talk to your specific users, understand their mental models, or make nuanced design decisions based on real behaviour. That's what we do.",
  },
  {
    q: "How fast do you turn things around?",
    a: "Our audit takes 1–3 days. Most projects complete within 1–3 weeks. We move fast because small teams can't afford to wait.",
  },
  {
    q: "What if I'm not sure which service I need?",
    a: "Book a free 15-minute check-in. We'll tell you exactly what's most valuable for your situation , even if it's something we don't offer.",
  },
  {
    q: "Do you work with non-technical founders?",
    a: "Absolutely. We explain everything in plain language. No jargon, no assumption of design knowledge. Just clear, actionable outputs.",
  },
  {
    q: "What does the monthly retainer actually include?",
    a: "Weekly small UX improvements, 1–2 design requests, one mini research or usability session per month, developer fixes, and priority async support. It's a real embedded UX team.",
  },
];
